import { useState, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../store/hooks.js';
import { selectTradingProviders, selectPreferredTradingProvider, setPreferredTradingProvider } from '../store/settingsSlice.js';
import { ProviderFactory, TRADING_PROVIDERS } from '../services/providers';
import type { TradingProviderType } from '../services/providers/types.js';
import { useToast } from '../hooks/useToast.js';

import { ProductType, OrderType, TransactionType } from '@ocktra/shared-types';

type ValidityType = 'DAY' | 'IOC';

interface OrderFormProps {
    symbol: string;
    onClose: () => void;
}

const OrderForm = ({ symbol, onClose }: OrderFormProps) => {
    const { showToast } = useToast();
    const dispatch = useAppDispatch();
    const tradingProviders = useAppSelector(selectTradingProviders);
    const preferredProvider = useAppSelector(selectPreferredTradingProvider);
    const connectedProviders = tradingProviders.filter(p => p.status === 'connected');
    const [side, setSide] = useState<TransactionType>(TransactionType.BUY);
    const [type, setType] = useState<OrderType>(OrderType.MARKET);
    const [quantity, setQuantity] = useState<number>(1);
    const [price, setPrice] = useState<number | undefined>();
    const [stopPrice, setStopPrice] = useState<number | undefined>();
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Use preferred provider if it's still connected, otherwise use first connected provider
    const getInitialProvider = (): TradingProviderType => {
        if (preferredProvider && connectedProviders.find(p => p.providerName === preferredProvider)) {
            return preferredProvider as TradingProviderType;
        }
        return connectedProviders.length > 0 ? (connectedProviders[0].providerName as TradingProviderType) : 'kite';
    };

    const [selectedProvider, setSelectedProvider] = useState<TradingProviderType>(getInitialProvider());
    const [product, setProduct] = useState<ProductType>(ProductType.DELIVERY);
    const [validity, setValidity] = useState<ValidityType>('DAY');
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [disclosedQty, setDisclosedQty] = useState<number | undefined>();

    // Close on Escape key
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [onClose]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            // Find the selected provider's connection details
            const providerConnection = connectedProviders.find(p => p.providerName === selectedProvider);

            if (!providerConnection) {
                throw new Error('Provider not connected');
            }

            const tradingProvider = ProviderFactory.getTradingProvider(selectedProvider);

            if (!tradingProvider) {
                throw new Error('Failed to get trading provider instance');
            }

            // Update connection details
            tradingProvider.updateConnectionDetails(providerConnection);

            // Place order
            const order = await tradingProvider.placeOrder({
                connId: providerConnection.id,
                tradingSymbol: symbol,
                transactionType: side,
                quantity,
                orderType: type,
                product,
                price
            });


            showToast(`Order placed via ${TRADING_PROVIDERS[selectedProvider].name}: ${side.toUpperCase()} ${quantity} ${symbol}`, 'success');
            console.log('[OrderForm] Order placed:', order);
            onClose();
        } catch (error) {
            console.error('[OrderForm] Error placing order:', error);
            showToast(`Error placing order: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const getProviderIcon = (providerType: TradingProviderType) => {
        const icons: Partial<Record<TradingProviderType, string>> = {
            papertrade: '🎮',
            kite: '🟢',
        };
        return icons[providerType] || '📊';
    };

    const requiresPrice = type === OrderType.LIMIT || type === OrderType.STOP_LOSS_MARKET;
    const requiresStopPrice = type === OrderType.STOP_LOSS || type === OrderType.STOP_LOSS_MARKET;

    const adjustQuantity = (delta: number) => {
        setQuantity(prev => Math.max(1, prev + delta));
    };

    const getOrderSummary = () => {
        const action = side === TransactionType.BUY ? 'Buy' : 'Sell';

        switch (type) {
            case OrderType.MARKET:
                return `${action} ${quantity} @ Market`;
            case OrderType.LIMIT:
                return price ? `${action} ${quantity} @ ₹${price}` : `${action} ${quantity} @ Limit`;
            case OrderType.STOP_LOSS:
                return stopPrice ? `${action} ${quantity} @ SL ₹${stopPrice}` : `${action} ${quantity} @ SL`;
            case OrderType.STOP_LOSS_MARKET:
                return stopPrice ? `${action} ${quantity} @ SL-M ₹${stopPrice}` : `${action} ${quantity} @ SL-M`;
            default:
                return `${action} ${quantity}`;
        }
    };

    return (
        <div
            onClick={onClose}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10000
            }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    backgroundColor: 'var(--color-surface)',
                    borderRadius: 'var(--radius-lg)',
                    padding: 0,
                    width: '90%',
                    maxWidth: '420px',
                    boxShadow: 'var(--shadow-xl)',
                    overflow: 'hidden'
                }}
            >
                {/* Header with Buy/Sell Toggle */}
                <div style={{
                    display: 'flex',
                    backgroundColor: 'var(--color-background)',
                    borderBottom: '1px solid var(--color-border)'
                }}>
                    <button
                        type="button"
                        onClick={() => setSide(TransactionType.BUY)}
                        style={{
                            flex: 1,
                            padding: 'var(--spacing-md)',
                            border: 'none',
                            backgroundColor: side === TransactionType.BUY ? 'var(--color-primary-600)' : 'transparent',
                            color: side === TransactionType.BUY ? 'white' : 'var(--color-text-secondary)',
                            fontWeight: '700',
                            fontSize: '0.875rem',
                            cursor: 'pointer',
                            transition: 'all 150ms ease',
                            borderBottom: side === TransactionType.BUY ? '3px solid var(--color-primary-700)' : '3px solid transparent'
                        }}
                    >
                        BUY
                    </button>
                    <button
                        type="button"
                        onClick={() => setSide(TransactionType.SELL)}
                        style={{
                            flex: 1,
                            padding: 'var(--spacing-md)',
                            border: 'none',
                            backgroundColor: side === TransactionType.SELL ? 'var(--color-danger-600)' : 'transparent',
                            color: side === TransactionType.SELL ? 'white' : 'var(--color-text-secondary)',
                            fontWeight: '700',
                            fontSize: '0.875rem',
                            cursor: 'pointer',
                            transition: 'all 150ms ease',
                            borderBottom: side === TransactionType.SELL ? '3px solid var(--color-danger-700)' : '3px solid transparent'
                        }}
                    >
                        SELL
                    </button>
                </div>

                {/* Symbol Header */}
                <div style={{
                    padding: 'var(--spacing-sm) var(--spacing-md)',
                    borderBottom: '1px solid var(--color-border-light)',
                    backgroundColor: 'var(--color-background)'
                }}>
                    <div style={{
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        color: 'var(--color-text-primary)'
                    }}>
                        {symbol}
                    </div>
                </div>

                {/* Provider Selection */}
                {connectedProviders.length > 1 && (
                    <div style={{
                        padding: 'var(--spacing-sm) var(--spacing-md)',
                        borderBottom: '1px solid var(--color-border-light)',
                        backgroundColor: 'var(--color-background)'
                    }}>
                        <div style={{ display: 'flex', gap: 'var(--spacing-xs)', flexWrap: 'wrap' }}>
                            {connectedProviders.map(provider => (
                                <button
                                    key={provider.providerName}
                                    type="button"
                                    onClick={() => {
                                        const providerName = provider.providerName as TradingProviderType;
                                        setSelectedProvider(providerName);
                                        dispatch(setPreferredTradingProvider(providerName));
                                    }}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        padding: '4px 8px',
                                        borderRadius: 'var(--radius-sm)',
                                        border: selectedProvider === provider.providerName ? '1.5px solid var(--color-primary-600)' : '1px solid var(--color-border)',
                                        backgroundColor: selectedProvider === provider.providerName ? 'var(--color-primary-50)' : 'white',
                                        color: selectedProvider === provider.providerName ? 'var(--color-primary-700)' : 'var(--color-text-secondary)',
                                        fontWeight: '500',
                                        fontSize: '0.6875rem',
                                        cursor: 'pointer',
                                        transition: 'all 150ms ease'
                                    }}
                                >
                                    <span style={{ fontSize: '0.75rem' }}>{getProviderIcon(provider.providerName as TradingProviderType)}</span>
                                    <span>{TRADING_PROVIDERS[provider.providerName as TradingProviderType].name}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                <form onSubmit={handleSubmit} style={{ padding: 'var(--spacing-md)' }}>
                    {/* Product Type & Validity */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 'var(--spacing-sm)',
                        marginBottom: 'var(--spacing-sm)'
                    }}>
                        <div>
                            <label style={{
                                display: 'block',
                                marginBottom: '4px',
                                fontSize: '0.6875rem',
                                fontWeight: '500',
                                color: 'var(--color-text-secondary)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                            }}>
                                Product
                            </label>
                            <select
                                value={product}
                                onChange={(e) => setProduct(e.target.value as ProductType)}
                                style={{
                                    width: '100%',
                                    padding: '8px',
                                    borderRadius: 'var(--radius-sm)',
                                    border: '1px solid var(--color-border)',
                                    backgroundColor: 'white',
                                    fontSize: '0.8125rem',
                                    fontWeight: '500',
                                    cursor: 'pointer',
                                    color: 'var(--color-text-primary)'
                                }}
                            >
                                <option value={ProductType.POSITIONAL}>POSITIONAL</option>
                                <option value={ProductType.INTRADAY}>INTRADAY</option>
                                <option value={ProductType.DELIVERY}>DELIVERY</option>
                            </select>
                        </div>
                        <div>
                            <label style={{
                                display: 'block',
                                marginBottom: '4px',
                                fontSize: '0.6875rem',
                                fontWeight: '500',
                                color: 'var(--color-text-secondary)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                            }}>
                                Validity
                            </label>
                            <select
                                value={validity}
                                onChange={(e) => setValidity(e.target.value as ValidityType)}
                                style={{
                                    width: '100%',
                                    padding: '8px',
                                    borderRadius: 'var(--radius-sm)',
                                    border: '1px solid var(--color-border)',
                                    backgroundColor: 'white',
                                    fontSize: '0.8125rem',
                                    fontWeight: '500',
                                    cursor: 'pointer',
                                    color: 'var(--color-text-primary)'
                                }}
                            >
                                <option value="DAY">Day</option>
                                <option value="IOC">IOC</option>
                            </select>
                        </div>
                    </div>

                    {/* Order Type */}
                    <div style={{ marginBottom: 'var(--spacing-sm)' }}>
                        <label style={{
                            display: 'block',
                            marginBottom: '4px',
                            fontSize: '0.6875rem',
                            fontWeight: '500',
                            color: 'var(--color-text-secondary)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                        }}>
                            Order Type
                        </label>
                        <div style={{ display: 'flex', gap: '6px' }}>
                            {([OrderType.MARKET, OrderType.LIMIT, OrderType.STOP_LOSS, OrderType.STOP_LOSS_MARKET] as OrderType[]).map((orderType) => (
                                <button
                                    key={orderType}
                                    type="button"
                                    onClick={() => setType(orderType)}
                                    style={{
                                        flex: 1,
                                        padding: '8px',
                                        borderRadius: 'var(--radius-sm)',
                                        border: type === orderType ? '1.5px solid var(--color-primary-600)' : '1px solid var(--color-border)',
                                        backgroundColor: type === orderType ? 'var(--color-primary-50)' : 'white',
                                        color: type === orderType ? 'var(--color-primary-700)' : 'var(--color-text-secondary)',
                                        fontWeight: '600',
                                        fontSize: '0.6875rem',
                                        cursor: 'pointer',
                                        transition: 'all 150ms ease',
                                        textTransform: 'uppercase'
                                    }}
                                >
                                    {orderType === OrderType.STOP_LOSS ? 'SL' : orderType === OrderType.STOP_LOSS_MARKET ? 'SL-M' : orderType}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Quantity with Quick Adjusters */}
                    <div style={{ marginBottom: 'var(--spacing-sm)' }}>
                        <label style={{
                            display: 'block',
                            marginBottom: '4px',
                            fontSize: '0.6875rem',
                            fontWeight: '500',
                            color: 'var(--color-text-secondary)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                        }}>
                            Quantity
                        </label>
                        <div style={{ display: 'flex', gap: '6px' }}>
                            <button
                                type="button"
                                onClick={() => adjustQuantity(-1)}
                                style={{
                                    width: '36px',
                                    padding: '8px',
                                    borderRadius: 'var(--radius-sm)',
                                    border: '1px solid var(--color-border)',
                                    backgroundColor: 'white',
                                    color: 'var(--color-text-primary)',
                                    fontWeight: '600',
                                    fontSize: '1rem',
                                    cursor: 'pointer',
                                    transition: 'all 150ms ease'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-gray-50)'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                            >
                                −
                            </button>
                            <input
                                type="number"
                                min="1"
                                value={quantity}
                                onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                                required
                                style={{
                                    flex: 1,
                                    padding: '8px',
                                    borderRadius: 'var(--radius-sm)',
                                    border: '1px solid var(--color-border)',
                                    backgroundColor: 'white',
                                    fontSize: '0.8125rem',
                                    fontWeight: '500',
                                    textAlign: 'center',
                                    color: 'var(--color-text-primary)'
                                }}
                            />
                            <button
                                type="button"
                                onClick={() => adjustQuantity(1)}
                                style={{
                                    width: '36px',
                                    padding: '8px',
                                    borderRadius: 'var(--radius-sm)',
                                    border: '1px solid var(--color-border)',
                                    backgroundColor: 'white',
                                    color: 'var(--color-text-primary)',
                                    fontWeight: '600',
                                    fontSize: '1rem',
                                    cursor: 'pointer',
                                    transition: 'all 150ms ease'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-gray-50)'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                            >
                                +
                            </button>
                        </div>
                    </div>

                    {/* Price Fields */}
                    {requiresPrice && (
                        <div style={{ marginBottom: 'var(--spacing-sm)' }}>
                            <label style={{
                                display: 'block',
                                marginBottom: '4px',
                                fontSize: '0.6875rem',
                                fontWeight: '500',
                                color: 'var(--color-text-secondary)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                            }}>
                                Price
                            </label>
                            <input
                                type="number"
                                step="0.05"
                                value={price || ''}
                                onChange={(e) => setPrice(parseFloat(e.target.value))}
                                required={requiresPrice}
                                placeholder="0.00"
                                style={{
                                    width: '100%',
                                    padding: '8px',
                                    borderRadius: 'var(--radius-sm)',
                                    border: '1px solid var(--color-border)',
                                    backgroundColor: 'white',
                                    fontSize: '0.8125rem',
                                    fontWeight: '500',
                                    color: 'var(--color-text-primary)'
                                }}
                            />
                        </div>
                    )}

                    {requiresStopPrice && (
                        <div style={{ marginBottom: 'var(--spacing-sm)' }}>
                            <label style={{
                                display: 'block',
                                marginBottom: '4px',
                                fontSize: '0.6875rem',
                                fontWeight: '500',
                                color: 'var(--color-text-secondary)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                            }}>
                                Trigger Price
                            </label>
                            <input
                                type="number"
                                step="0.05"
                                value={stopPrice || ''}
                                onChange={(e) => setStopPrice(parseFloat(e.target.value))}
                                required={requiresStopPrice}
                                placeholder="0.00"
                                style={{
                                    width: '100%',
                                    padding: '8px',
                                    borderRadius: 'var(--radius-sm)',
                                    border: '1px solid var(--color-border)',
                                    backgroundColor: 'white',
                                    fontSize: '0.8125rem',
                                    fontWeight: '500',
                                    color: 'var(--color-text-primary)'
                                }}
                            />
                        </div>
                    )}

                    {/* Advanced Options Toggle */}
                    <div style={{ marginBottom: 'var(--spacing-sm)' }}>
                        <button
                            type="button"
                            onClick={() => setShowAdvanced(!showAdvanced)}
                            style={{
                                background: 'none',
                                border: 'none',
                                padding: '4px 0',
                                color: 'var(--color-primary-600)',
                                fontSize: '0.75rem',
                                fontWeight: '500',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                            }}
                        >
                            <span>{showAdvanced ? '▼' : '▶'}</span>
                            <span>Advanced options</span>
                        </button>

                        {showAdvanced && (
                            <div style={{ marginTop: 'var(--spacing-sm)' }}>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '4px',
                                    fontSize: '0.6875rem',
                                    fontWeight: '500',
                                    color: 'var(--color-text-secondary)',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                }}>
                                    Disclosed Qty (Optional)
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    value={disclosedQty || ''}
                                    onChange={(e) => setDisclosedQty(parseInt(e.target.value) || undefined)}
                                    placeholder="0"
                                    style={{
                                        width: '100%',
                                        padding: '8px',
                                        borderRadius: 'var(--radius-sm)',
                                        border: '1px solid var(--color-border)',
                                        backgroundColor: 'white',
                                        fontSize: '0.8125rem',
                                        fontWeight: '500',
                                        color: 'var(--color-text-primary)'
                                    }}
                                />
                            </div>
                        )}
                    </div>

                    {/* Submit Button */}
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        style={{
                            width: '100%',
                            padding: '12px',
                            borderRadius: 'var(--radius-sm)',
                            border: 'none',
                            backgroundColor: side === TransactionType.BUY
                                ? (isSubmitting ? 'var(--color-primary-400)' : 'var(--color-primary-600)')
                                : (isSubmitting ? 'var(--color-danger-400)' : 'var(--color-danger-600)'),
                            color: 'white',
                            fontWeight: '700',
                            fontSize: '0.875rem',
                            cursor: isSubmitting ? 'not-allowed' : 'pointer',
                            transition: 'all 150ms ease',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                        }}
                        onMouseEnter={(e) => {
                            if (!isSubmitting) {
                                e.currentTarget.style.backgroundColor = side === TransactionType.BUY
                                    ? 'var(--color-primary-700)'
                                    : 'var(--color-danger-700)';
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (!isSubmitting) {
                                e.currentTarget.style.backgroundColor = side === TransactionType.BUY
                                    ? 'var(--color-primary-600)'
                                    : 'var(--color-danger-600)';
                            }
                        }}
                    >
                        {isSubmitting ? 'Placing Order...' : getOrderSummary()}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default OrderForm;
